import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

const Section = ({ nr, title, children }) => (
  <div>
    <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
      <span className="w-6 h-6 rounded-md bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] text-xs font-black shrink-0">{nr}</span>
      {title}
    </h2>
    <div className="text-zinc-400 text-sm leading-relaxed space-y-2 pl-8">{children}</div>
  </div>
);

export default function AlgemeneVoorwaardenPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
            <span className="text-white font-bold tracking-wide">FitSmith</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-14">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
            <FileText size={20} className="text-[#C9A84C]" />
          </div>
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-[3px] mb-1">Juridisch</p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl">Algemene Voorwaarden</h1>
          </div>
        </div>
        <p className="text-zinc-600 text-sm mb-10 pl-14">Versie januari 2026 · FitSmith</p>

        <div className="space-y-8">

          <Section nr="1" title="Bedrijfsgegevens">
            <p>Deze Algemene Voorwaarden zijn van toepassing op alle producten en diensten aangeboden door:</p>
            <div className="bg-[#0f0f0f] border border-white/8 rounded-xl px-5 py-4 space-y-1">
              <p><span className="text-white font-semibold">Bedrijfsnaam:</span> FitSmith</p>
              <p><span className="text-white font-semibold">KvK-nummer:</span> 42035017</p>
              <p><span className="text-white font-semibold">E-mail:</span>{" "}
                <a href="mailto:info@fitsmith.nl" className="text-[#C9A84C] hover:underline">info@fitsmith.nl</a>
              </p>
            </div>
          </Section>

          <Section nr="2" title="Toepasselijkheid">
            <p>
              Deze voorwaarden zijn van toepassing op alle aanbiedingen, bestellingen en overeenkomsten die via de website van FitSmith tot stand komen. Door een product of dienst af te nemen, ga je akkoord met deze voorwaarden. Afwijkingen zijn alleen geldig indien schriftelijk overeengekomen.
            </p>
          </Section>

          <Section nr="3" title="Aanbod en diensten">
            <p>FitSmith biedt de volgende producten en diensten aan:</p>
            <ul className="space-y-1.5 list-none">
              {[
                "Digitale trainingsschema's (direct downloadbaar of toegankelijk via account)",
                "Persoonlijk op maat gemaakte trainingsschema's op basis van een intakeformulier",
                "Online coaching en begeleiding via het platform",
                "Toegang tot cursusmateriaal en leerprogramma's",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>
              Alle weergegeven prijzen zijn inclusief eventueel verschuldigde belastingen, tenzij anders vermeld. FitSmith behoudt zich het recht voor het aanbod te wijzigen of in te trekken.
            </p>
          </Section>

          <Section nr="4" title="Betalingen">
            <p>
              Betaling geschiedt vooraf via de website, middels de beschikbare betaalmethoden (zoals iDEAL of creditcard), aangeboden via betalingsprovider Mollie. Na ontvangst van de betaling wordt het digitale product direct beschikbaar gesteld.
            </p>
            <p>
              FitSmith is niet aansprakelijk voor storingen bij de betalingsprovider. Bij twijfel over een betaling kun je contact opnemen via{" "}
              <a href="mailto:info@fitsmith.nl" className="text-[#C9A84C] hover:underline">info@fitsmith.nl</a>.
            </p>
          </Section>

          <Section nr="5" title="Herroepingsrecht en restitutie">
            <p>
              Op grond van artikel 16 van de Richtlijn 2011/83/EU en artikel 6:230p BW geldt dat het herroepingsrecht niet van toepassing is op digitale inhoud die niet op een materiële drager wordt geleverd, indien de uitvoering is begonnen met uitdrukkelijke voorafgaande toestemming van de consument en de consument heeft erkend dat hij daardoor zijn herroepingsrecht verliest.
            </p>
            <div className="bg-[#0f0f0f] border border-[#C9A84C]/20 rounded-xl px-5 py-4">
              <p className="font-semibold text-white mb-1">Concreet betekent dit:</p>
              <p>
                Door de aankoop te bevestigen en uitdrukkelijk akkoord te gaan bij de checkout, doe je afstand van je 14 dagen bedenktijd. Het digitale product wordt direct na betaling geleverd en kan niet worden geretourneerd of terugbetaald.
              </p>
            </div>
            <p>
              Voor uitzonderingen (bij aantoonbare technische problemen waarbij levering niet mogelijk is) verwijzen wij naar ons{" "}
              <a href="/restitutiebeleid" className="text-[#C9A84C] hover:underline">restitutiebeleid</a>.
            </p>
          </Section>

          <Section nr="6" title="Aansprakelijkheid">
            <p>
              FitSmith biedt trainingsschema's en coachingsinformatie aan als hulpmiddel. Het gebruik van onze producten en diensten geschiedt volledig op eigen risico van de gebruiker.
            </p>
            <ul className="space-y-1.5 list-none">
              {[
                "FitSmith is niet aansprakelijk voor blessures, schade aan eigendommen of gezondheidsklachten die voortvloeien uit het gebruik van onze producten.",
                "Wij garanderen geen specifieke resultaten. Resultaten hangen af van individuele inzet, lichamelijke gesteldheid en omstandigheden.",
                "Raadpleeg bij twijfel over je gezondheid altijd een (sport)arts voordat je begint met een trainingsprogramma.",
                "FitSmith is niet aansprakelijk voor indirecte schade, gederfde winst of gevolgschade.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </Section>

          <Section nr="7" title="Gebruik van producten">
            <p>
              Alle producten van FitSmith zijn uitsluitend bestemd voor persoonlijk gebruik. Het is strikt verboden om:
            </p>
            <ul className="space-y-1.5 list-none">
              {[
                "Producten, schema's of cursusmateriaal te delen, reproduceren of verspreiden zonder schriftelijke toestemming van FitSmith.",
                "Producten door te verkopen aan derden.",
                "Content te gebruiken voor commerciële doeleinden.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>
              Inbreuk op de intellectuele eigendomsrechten van FitSmith kan leiden tot juridische stappen.
            </p>
          </Section>

          <Section nr="8" title="Account en persoonsgegevens">
            <p>
              De klant is verantwoordelijk voor het opgeven van correcte en volledige gegevens bij registratie en bestelling. Een account is strikt persoonlijk en mag niet worden gedeeld met derden.
            </p>
            <p>
              FitSmith gaat zorgvuldig om met persoonsgegevens conform de AVG (Algemene Verordening Gegevensbescherming). Zie onze{" "}
              <a href="/privacybeleid" className="text-[#C9A84C] hover:underline">Privacy Policy</a>{" "}
              voor meer informatie.
            </p>
          </Section>

          <Section nr="9" title="Wijzigingen">
            <p>
              FitSmith behoudt zich het recht voor deze Algemene Voorwaarden op ieder moment aan te passen. Wijzigingen worden gepubliceerd op de website. Het is de verantwoordelijkheid van de gebruiker om de voorwaarden regelmatig te raadplegen. Voortgezet gebruik van de diensten na publicatie van wijzigingen geldt als acceptatie.
            </p>
          </Section>

          <Section nr="10" title="Toepasselijk recht en geschillen">
            <p>
              Op alle overeenkomsten tussen FitSmith en de klant is Nederlands recht van toepassing. Geschillen worden in eerste instantie minnelijk opgelost. Indien dit niet mogelijk is, worden geschillen voorgelegd aan de bevoegde rechter in het arrondissement waar FitSmith gevestigd is.
            </p>
          </Section>

          <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl px-6 py-5">
            <p className="text-zinc-500 text-sm">
              Vragen over deze voorwaarden? Neem contact op via{" "}
              <a href="mailto:info@fitsmith.nl" className="text-[#C9A84C] hover:underline font-semibold">info@fitsmith.nl</a>.
            </p>
          </div>
        </div>

        <button onClick={() => navigate(-1)}
          className="mt-12 flex items-center gap-2 text-zinc-500 hover:text-[#C9A84C] text-sm transition-colors">
          <ArrowLeft size={14} /> Terug
        </button>
      </div>

      <div className="border-t border-white/5 py-6 text-center">
        <p className="text-zinc-700 text-xs">© 2026 FitSmith. Alle rechten voorbehouden.</p>
      </div>
    </div>
  );
}
